import { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL

const COLORES_NIVEL = {
  A1:'#22C55E', A2:'#3B82F6', B1:'#8B5CF6',
  B2:'#F59E0B', C1:'#EF4444', C2:'#1B3A8C'
}

const NIVELES = ['All', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']

const TEMAS_ICONOS = {
  'Greetings':  '👋',
  'Numbers':    '🔢',
  'Vocabulary': '📖',
  'Family':     '👨‍👩‍👧',
  'Calendar':   '📅',
  'Grammar':    '📝',
  'Food':       '🍽️',
  'Directions': '🗺️',
  'Shopping':   '🛍️',
  'Health':     '🏥',
  'Culture':    '🇨🇴',
  'Business':   '💼',
  'Writing':    '✍️',
  'Speaking':   '🎤',
}

const TX = {
  es: {
    sinPlan: 'Este contenido está disponible solo para los planes Advanced y Mastery.',
    descargandoMsg: '✅ Descargando: ', noDisponible: 'El archivo aún no está disponible. ¡Muy pronto!',
    subtitulo: 'Hojas de práctica descargables para cada nivel. Imprímelas o úsalas en digital.',
    disponibles: 'worksheets disponibles', niveles: 'Niveles A1 a C1', planes: 'Planes Advanced y Mastery',
    filtroNivel: 'Filtrar por nivel', filtroTema: 'Filtrar por tema', todos: 'Todos',
    mostrando: 'Mostrando', de: 'de', ninguno: 'No se encontraron worksheets con este filtro.',
    pagina: 'página', paginas: 'páginas', descargar: '⬇️ Descargar', descargando: 'Descargando...',
    infoTitulo: 'Disponible en los planes Advanced y Mastery',
    infoTexto: 'Los worksheets están incluidos en EspañolGo Advanced ($289/8 meses) y EspañolGo Mastery ($459/año).'
  },
  en: {
    sinPlan: 'This content is available for Advanced and Mastery plans only.',
    descargandoMsg: '✅ Downloading: ', noDisponible: 'File not available yet. Coming soon!',
    subtitulo: 'Downloadable practice sheets for every level. Print them or use them digitally.',
    disponibles: 'worksheets available', niveles: 'A1 to C1 levels', planes: 'Advanced & Mastery plans',
    filtroNivel: 'Filter by Level', filtroTema: 'Filter by Topic', todos: 'All',
    mostrando: 'Showing', de: 'of', ninguno: 'No worksheets found for this filter.',
    pagina: 'page', paginas: 'pages', descargar: '⬇️ Download', descargando: 'Downloading...',
    infoTitulo: 'Available for Advanced & Mastery plans',
    infoTexto: 'Worksheets are included in EspañolGo Advanced ($289/8mo) and EspañolGo Mastery ($459/year).'
  }
}

// Nombre del tema en espanol (los temas vienen de la base de datos en ingles)
const TEMAS_ES = {
  'Greetings':'Saludos', 'Numbers':'Números', 'Vocabulary':'Vocabulario', 'Family':'Familia', 'Calendar':'Calendario',
  'Grammar':'Gramática', 'Food':'Comida', 'Directions':'Direcciones', 'Shopping':'Compras', 'Health':'Salud',
  'Culture':'Cultura', 'Business':'Negocios', 'Writing':'Escritura', 'Speaking':'Conversación'
}

export default function Worksheets({ usuario, idioma = 'es' }) {
  const tx = TX[idioma] || TX.es
  const nombreTema = tema => (idioma === 'es' && TEMAS_ES[tema]) ? TEMAS_ES[tema] : tema
  const [worksheets,   setWorksheets]   = useState([])
  const [cargando,     setCargando]     = useState(true)
  const [filtroNivel,  setFiltroNivel]  = useState('All')
  const [filtroTema,   setFiltroTema]   = useState('All')
  const [descargando,  setDescargando]  = useState(null)
  const [mensaje,      setMensaje]      = useState(null)

  // Verificar si el usuario tiene plan Advanced o Mastery
  // Por ahora permitimos acceso — cuando integres planes puedes restringir aquí
  const tieneAcceso = true

  useEffect(() => {
    cargarWorksheets()
  }, [])

  async function cargarWorksheets() {
    setCargando(true)
    try {
      const res = await axios.get(`${API}/curriculo/worksheets`)
      setWorksheets(res.data.datos || [])
    } catch (err) {
      console.error(err)
    } finally {
      setCargando(false)
    }
  }

  async function descargar(ws) {
    if (!tieneAcceso) {
      setMensaje({ tipo:'error', texto: tx.sinPlan })
      setTimeout(() => setMensaje(null), 3000)
      return
    }
    setDescargando(ws.id)
    try {
      const res = await axios.get(`${API}/curriculo/worksheets/descargar/${ws.id}`)
      if (res.data.ok && res.data.url) {
        const link = document.createElement('a')
        link.href = res.data.url
        link.download = ws.archivo_nombre || ws.titulo + '.pdf'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setMensaje({ tipo:'ok', texto: tx.descargandoMsg + ws.titulo })
      } else {
        setMensaje({ tipo:'error', texto: tx.noDisponible })
      }
    } catch (err) {
      setMensaje({ tipo:'error', texto: tx.noDisponible })
    } finally {
      setDescargando(null)
      setTimeout(() => setMensaje(null), 3000)
    }
  }

  const temas = ['All', ...new Set(worksheets.map(w => w.tema))]

  const filtradas = worksheets.filter(w => {
    const porNivel = filtroNivel === 'All' || w.nivel === filtroNivel
    const porTema  = filtroTema  === 'All' || w.tema  === filtroTema
    return porNivel && porTema
  })

  if (cargando) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, color:'#6B7280' }}>
      <div style={{ width:32, height:32, border:'3px solid #E2E6F0', borderTopColor:'#1B3A8C', borderRadius:'50%', animation:'spinner .8s linear infinite' }} />
    </div>
  )

  return (
    <div style={{ maxWidth:900, margin:'0 auto', animation:'fadeIn .3s ease both' }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:16, padding:28, marginBottom:24, color:'#fff', display:'flex', alignItems:'center', gap:20 }}>
        <div style={{ fontSize:52 }}>📄</div>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:24, fontWeight:900, marginBottom:4 }}>Worksheets</h1>
          <p style={{ fontSize:14, opacity:.8 }}>
            {tx.subtitulo}
          </p>
          <div style={{ display:'flex', gap:12, marginTop:12, flexWrap:'wrap' }}>
            <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:8, padding:'4px 12px', fontSize:12, fontWeight:600 }}>
              📚 {worksheets.length} {tx.disponibles}
            </div>
            <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:8, padding:'4px 12px', fontSize:12, fontWeight:600 }}>
              🎯 {tx.niveles}
            </div>
            <div style={{ background:'rgba(245,158,11,0.3)', border:'1px solid rgba(245,158,11,0.5)', borderRadius:8, padding:'4px 12px', fontSize:12, fontWeight:600, color:'#FDE68A' }}>
              💎 {tx.planes}
            </div>
          </div>
        </div>
      </div>

      {/* Mensaje */}
      {mensaje && (
        <div style={{ padding:'10px 16px', borderRadius:10, marginBottom:16, fontSize:14, fontWeight:600,
          background: mensaje.tipo==='ok' ? '#F0FDF4' : '#FEF2F2',
          color: mensaje.tipo==='ok' ? '#166534' : '#991B1B',
          border:`1px solid ${mensaje.tipo==='ok' ? '#22C55E' : '#EF4444'}` }}>
          {mensaje.texto}
        </div>
      )}

      {/* Filtros */}
      <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20, marginBottom:20 }}>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#6B7280', marginBottom:8, textTransform:'uppercase', letterSpacing:1 }}>
            {tx.filtroNivel}
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {NIVELES.map(n => (
              <button key={n} onClick={() => setFiltroNivel(n)}
                style={{ padding:'6px 16px', borderRadius:999, border:`2px solid ${filtroNivel===n ? (COLORES_NIVEL[n] || '#1B3A8C') : '#E2E6F0'}`,
                  background: filtroNivel===n ? (COLORES_NIVEL[n] || '#1B3A8C') : '#fff',
                  color: filtroNivel===n ? '#fff' : '#374151',
                  fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                {n === 'All' ? tx.todos : n}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:'#6B7280', marginBottom:8, textTransform:'uppercase', letterSpacing:1 }}>
            {tx.filtroTema}
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {temas.map(t => (
              <button key={t} onClick={() => setFiltroTema(t)}
                style={{ padding:'6px 14px', borderRadius:999, border:`2px solid ${filtroTema===t ? '#1B3A8C' : '#E2E6F0'}`,
                  background: filtroTema===t ? '#1B3A8C' : '#fff',
                  color: filtroTema===t ? '#fff' : '#374151',
                  fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                {TEMAS_ICONOS[t] && <span>{TEMAS_ICONOS[t]}</span>}
                {t === 'All' ? tx.todos : nombreTema(t)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contador */}
      <div style={{ fontSize:13, color:'#6B7280', marginBottom:16 }}>
        {tx.mostrando} <strong>{filtradas.length}</strong> {tx.de} {worksheets.length} worksheets
      </div>

      {/* Grid de worksheets */}
      {filtradas.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:'#9CA3AF' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
          <p style={{ fontSize:16 }}>{tx.ninguno}</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:16 }}>
          {filtradas.map(ws => (
            <div key={ws.id} style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, overflow:'hidden', transition:'all .2s ease' }}
              onMouseOver={e => { e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.08)'; e.currentTarget.style.transform='translateY(-2px)' }}
              onMouseOut={e =>  { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none' }}>

              {/* Header de la card */}
              <div style={{ background:`linear-gradient(135deg,${COLORES_NIVEL[ws.nivel] || '#1B3A8C'}22,${COLORES_NIVEL[ws.nivel] || '#1B3A8C'}11)`, borderBottom:'1px solid #E2E6F0', padding:'14px 18px', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ fontSize:28 }}>{TEMAS_ICONOS[ws.tema] || '📄'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ background:COLORES_NIVEL[ws.nivel] || '#1B3A8C', color:'#fff', fontSize:10, fontWeight:800, padding:'2px 8px', borderRadius:999 }}>
                      {ws.nivel}
                    </span>
                    <span style={{ fontSize:11, color:'#6B7280', background:'#F4F6FA', padding:'2px 8px', borderRadius:6, fontWeight:600 }}>
                      {nombreTema(ws.tema)}
                    </span>
                  </div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#1A1A2E', marginTop:4 }}>{ws.titulo}</div>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding:'14px 18px' }}>
                {ws.descripcion && (
                  <p style={{ fontSize:13, color:'#6B7280', lineHeight:1.6, marginBottom:12 }}>{ws.descripcion}</p>
                )}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ display:'flex', gap:12 }}>
                    <span style={{ fontSize:12, color:'#9CA3AF' }}>📄 {ws.paginas} {ws.paginas > 1 ? tx.paginas : tx.pagina}</span>
                    <span style={{ fontSize:12, color:'#9CA3AF' }}>🆓 PDF</span>
                  </div>
                  <button
                    onClick={() => descargar(ws)}
                    disabled={descargando === ws.id}
                    style={{ display:'flex', alignItems:'center', gap:6, background: descargando === ws.id ? '#E2E6F0' : '#1B3A8C', color: descargando === ws.id ? '#9CA3AF' : '#fff', border:'none', borderRadius:10, padding:'8px 16px', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor: descargando === ws.id ? 'not-allowed' : 'pointer', transition:'all .15s' }}>
                    {descargando === ws.id ? (
                      <>
                        <div style={{ width:14, height:14, border:'2px solid #9CA3AF', borderTopColor:'#6B7280', borderRadius:'50%', animation:'spinner .8s linear infinite' }} />
                        {tx.descargando}
                      </>
                    ) : (
                      <>{tx.descargar}</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info plan */}
      <div style={{ marginTop:24, background:'linear-gradient(135deg,#92400E11,#F59E0B11)', border:'1px solid #F59E0B44', borderRadius:14, padding:20, display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:32 }}>💎</div>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'#92400E', marginBottom:4 }}>
            {tx.infoTitulo}
          </div>
          <div style={{ fontSize:13, color:'#6B7280' }}>
            {tx.infoTexto}
          </div>
        </div>
      </div>
    </div>
  )
}
